// Section.js
// -----------------------------------------------------------------------------
// Vertical content section wrapper. Provides consistent vertical rhythm, an
// optional background variant, and a centered max-width container. This is the
// primary page building block: wrap each page region in a <Section>.
//
// Props:
//   - bg        : 'default'|'tint'|'brand'|'accent'|'dark'  (default 'default')
//                 background treatment; sets a coherent text color too.
//   - padding   : 'sm'|'md'|'lg'|'none'   (default 'md')  vertical padding scale
//   - maxWidth  : 'sm'|'md'|'lg'|'xl'     (default 'lg')  inner container width
//   - id        : string                  (default undef) anchor id for nav
//   - sx        : object                  (default {})    extra Joy sx overrides
//   - children  : node                    (required)      section content
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/joy/Box';
import Container from './Container';

const PADDING_SCALE = {
  none: 0,
  sm: { xs: 4, md: 6 },
  md: { xs: 7, md: 10 },
  lg: { xs: 10, md: 16 },
};

// Dark ink for the accent (orange) surface. Per the shared palette spec, accent
// 500 and lighter need dark text for AA contrast; no Joy token maps to it.
const ACCENT_CONTRAST_TEXT = '#1A1A1A';

const BG_VARIANTS = {
  default: { bgcolor: 'background.surface', color: 'text.primary' },
  tint: { bgcolor: 'background.level1', color: 'text.primary' },
  brand: { bgcolor: 'primary.500', color: 'common.white' },
  accent: { bgcolor: 'accent.500', color: ACCENT_CONTRAST_TEXT },
  dark: { bgcolor: 'primary.800', color: 'common.white' },
};

const Section = ({
  bg = 'default',
  padding = 'md',
  maxWidth = 'lg',
  id,
  sx = {},
  children,
}) => {
  // Zero-trust: constrain enum-like props to known sets.
  const variant = BG_VARIANTS[bg] || BG_VARIANTS.default;
  const pad = padding in PADDING_SCALE ? PADDING_SCALE[padding] : PADDING_SCALE.md;

  return (
    <Box
      component="section"
      id={id}
      sx={{
        width: '100%',
        py: pad,
        ...variant,
        ...sx,
      }}
    >
      <Container maxWidth={maxWidth}>{children}</Container>
    </Box>
  );
};

Section.propTypes = {
  bg: PropTypes.oneOf(['default', 'tint', 'brand', 'accent', 'dark']),
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  maxWidth: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  id: PropTypes.string,
  sx: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default Section;
